import { Button, CheckboxCard, CheckboxWrapper, Divider } from '@design-system';
import { normaliseVariantName } from '@utils/frames';
import { Trans, useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';

import { CurrencyCode } from '../../../types/Locations';
import { Frame, FrameOptionConsolidated } from '../../../types/Products';
import { Image } from '../../Image';
import { ProductPrice } from '../../ProductPrice/ProductPrice';
import { useFramingGuides } from './useFramingGuides';

export interface FramePickerProps {
  currencyCode?: CurrencyCode;
  description: string;
  frameByDefault?: boolean;
  frameOptions: Array<FrameOptionConsolidated>;
  frameSelected: Frame | null;
  hideSubmitButton: boolean;
  hideTitle: boolean;
  includeUnframed: boolean;
  onFrameSelected?: (variant: Frame | null) => void;
  onSubmit?: (variant: Frame | null) => void;
  onVariantSelected?: (variant: Frame | null) => void;
  productSubtotal: number;
  submitButtonLabel: string;
}

const FramePicker = ({
  currencyCode,
  description,
  frameByDefault,
  frameOptions,
  frameSelected,
  hideSubmitButton,
  hideTitle,
  includeUnframed,
  onFrameSelected,
  onSubmit,
  onVariantSelected,
  productSubtotal,
  submitButtonLabel,
}: FramePickerProps) => {
  const { t } = useTranslation(['product', 'countries']);
  const { handleShowGlazingGuide, handleShowMountingGuide } =
    useFramingGuides();
  const checkboxGroupNameFrame = useMemo(() => crypto.randomUUID(), []);
  const checkboxGroupNameGlazing = useMemo(() => crypto.randomUUID(), []);

  const [selectedFrame, setSelectedFrame] =
    useState<FrameOptionConsolidated | null>(
      frameOptions.find(({ title }) => title === frameSelected?.title) || null,
    );
  const [selectedVariant, setSelectedVariant] = useState<string | null>(
    frameSelected?.variant.title ?? null,
  );
  const [frame, setFrame] = useState<Frame | null>(frameSelected);

  const framePrice = useMemo(() => {
    if (!frame) return 0;

    return frame.variant.consolidatedVariants.reduce(
      (acc, { price, quantity }) => acc + price * quantity,
      0,
    );
  }, [frame]);

  useEffect(() => {
    if (selectedFrame === null) {
      setFrame(null);
      return;
    }
    const { variants: submitVariants, ...selectedFramesRest } = selectedFrame;
    const submitVariant = submitVariants.find(
      ({ title }) => title === selectedVariant,
    );

    if (!submitVariant) {
      setFrame(null);
      return;
    }

    const newFrame = {
      ...selectedFramesRest,
      variant: submitVariant,
    };

    setFrame(newFrame);

    if (onFrameSelected) {
      onFrameSelected(newFrame);
    }

    if (onVariantSelected) {
      onVariantSelected(newFrame);
    }
  }, [selectedFrame, selectedVariant, onFrameSelected, onVariantSelected]);

  const subtotal = selectedVariant
    ? productSubtotal +
      (frame?.variant.consolidatedVariants.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0,
      ) ?? 0)
    : productSubtotal;

  useEffect(() => {
    if (onFrameSelected) {
      onFrameSelected(frame);
    }
  }, [frame, onFrameSelected]);

  useEffect(() => {
    if (frameSelected === null) {
      setSelectedFrame(frameOptions.length > 0 ? frameOptions[0] : null);
      setSelectedVariant(
        frameOptions.length > 0 ? frameOptions[0].variants[0].title : null,
      );
      return;
    }

    const newSelectedFrame =
      frameOptions.find(({ title }) => title === frameSelected.title) || null;
    setSelectedFrame(newSelectedFrame);
    setSelectedVariant(frameSelected.variant.title);
  }, [frameOptions, frameSelected]);

  useEffect(() => {
    if (!frameByDefault) return;

    if (selectedFrame === null && frameOptions.length > 0) {
      const [defaultFrame] = frameOptions;
      setSelectedFrame(defaultFrame);
      setSelectedVariant(defaultFrame.variants[0].title);
    }
  }, [frameByDefault, frameOptions, selectedFrame]);

  const parseVariant = (title: string) => {
    const [glazing, mounting] = title.split('/').map((s) => s.trim());
    return { glazing, mounting: mounting || 'Standard' }; // Fallback if no '/'
  };

  const initialParts = parseVariant(
    frameSelected?.variant.title || selectedFrame?.variants[0]?.title || '',
  );

  const [activeGlazing, setActiveGlazing] = useState<string>(
    initialParts.glazing,
  );
  const [activeMounting, setActiveMounting] = useState<string>(
    initialParts.mounting,
  );

  useEffect(() => {
    if (!selectedVariant) return;
    const parts = parseVariant(selectedVariant);
    setActiveGlazing(parts.glazing);
    setActiveMounting(parts.mounting);
  }, [selectedVariant]);

  useEffect(() => {
    const combinedTitle = `${activeGlazing} / ${activeMounting}`;

    const match =
      selectedFrame?.variants.find((v) => v.title === combinedTitle) ||
      selectedFrame?.variants.find((v) => v.title.startsWith(activeGlazing));

    if (match) {
      setSelectedVariant(match.title);
    }
  }, [activeGlazing, activeMounting, selectedFrame]);

  const glazingGroups = useMemo(() => {
    if (!selectedFrame) return {};

    return selectedFrame.variants.reduce(
      (acc, variant) => {
        const { glazing, mounting } = parseVariant(variant.title);
        if (!acc[glazing]) acc[glazing] = [];
        acc[glazing].push({ ...variant, mountingLabel: mounting });
        return acc;
      },
      {} as Record<
        string,
        (FrameOptionConsolidated['variants'][number] & {
          mountingLabel: string;
        })[]
      >,
    );
  }, [selectedFrame]);

  const glazingOptions = Object.keys(glazingGroups);

  const showDescriptions = description.length > 0;

  const hasMountingOptions = (glazingGroups[activeGlazing]?.length ?? 0) > 1;

  return (
    <>
      {!hideTitle ? (
        <div className="mb-6 hidden w-full flex-1 md:flex">
          <p className="text-md font-bold md:text-lg">
            {t('drawers.review.framing.title')}
          </p>
        </div>
      ) : null}
      <div className="flex flex-col gap-9">
        <div className="flex flex-col gap-4">
          <div>
            <p className="font-bold">{t('drawers.review.framing.subtitle')}</p>
            {showDescriptions ? <p>{description}</p> : null}
          </div>

          <CheckboxWrapper>
            {frameOptions.map((frameOption) => {
              const selectedFrameOption = frameOptions.find(
                (fo) => fo.title === frame?.title,
              );
              const checked = frameOption.title === selectedFrameOption?.title;

              const cheapestUpgradeOption = Math.min(
                ...frameOption.variants.map((variant) =>
                  variant.consolidatedVariants.reduce(
                    (acc, { price, quantity }) => acc + price * quantity,
                    0,
                  ),
                ),
              );

              const cheapestUpgradeSelected = selectedFrameOption
                ? Math.min(
                    ...selectedFrameOption.variants.map((v) =>
                      v.consolidatedVariants.reduce(
                        (acc, { price, quantity }) => acc + price * quantity,
                        0,
                      ),
                    ),
                  )
                : 0;
              const price = cheapestUpgradeOption - cheapestUpgradeSelected;

              return (
                <CheckboxCard
                  key={`frameOptions-${checkboxGroupNameFrame}-${frameOption.title}`}
                  checked={checked}
                  description={frameOption.description}
                  id={`frameOptions-${checkboxGroupNameFrame}-${frameOption.title}`}
                  label={frameOption.title}
                  name={`frameOptions-${checkboxGroupNameFrame}`}
                  onChange={() => {
                    setSelectedFrame(frameOption);
                  }}
                  slot={
                    <div className="grid w-full grid-cols-2 gap-3">
                      {frameOption.images.slice(0, 2).map((image) => (
                        <Image
                          key={image.src}
                          alt={image.alt}
                          src={image.src}
                          width={384}
                        />
                      ))}
                    </div>
                  }
                  slotVisibleOnChecked
                  suffix={
                    checked || price === 0 ? undefined : (
                      <ProductPrice
                        currencyCode={currencyCode}
                        prefix={price >= 0 ? '+' : '-'}
                        price={Math.abs(price)}
                        skipConvertCurrency={!!currencyCode}
                      />
                    )
                  }
                  tag={
                    frameOption.recommended && frameOption.recommendedLabel
                      ? frameOption.recommendedLabel
                      : undefined
                  }
                  type="radio"
                  value={frameOption.title}
                />
              );
            })}
          </CheckboxWrapper>
        </div>

        {selectedFrame && selectedFrame.variants.length > 1 && (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <p className="w-full font-bold">
                  {t('drawers.review.framing.upgrade.title')}
                </p>

                <button
                  className="hyperlink shrink-0"
                  onClick={handleShowGlazingGuide}
                  type="button"
                >
                  {t('drawers.review.framing.upgrade.guideLink')}
                </button>
              </div>
              <CheckboxWrapper>
                {glazingOptions.map((gName) => {
                  const isChecked = activeGlazing === gName;
                  const [baseVariant] = glazingGroups[gName];
                  const baseVariantTotal =
                    baseVariant.consolidatedVariants.reduce(
                      (sum, variant) =>
                        sum + variant.price * (variant.quantity ?? 1),
                      0,
                    );
                  const price = baseVariantTotal - framePrice;

                  const normalisedVariantName = normaliseVariantName(gName);
                  const frameVariantFeatures = t(
                    `drawers.review.framing.upgrade.${normalisedVariantName}.detailsCheckbox`,
                    {
                      returnObjects: true,
                    },
                  );
                  const frameFeatures = Array.isArray(frameVariantFeatures)
                    ? frameVariantFeatures.filter(
                        (feature): feature is string =>
                          typeof feature === 'string',
                      )
                    : undefined;

                  return (
                    <CheckboxCard
                      key={gName}
                      checked={isChecked}
                      features={frameFeatures}
                      id={`glazingOptions-${checkboxGroupNameGlazing}-${gName}`}
                      label={gName}
                      name={`glazingOptions-${checkboxGroupNameGlazing}`}
                      onChange={() => {
                        setActiveGlazing(gName);
                        if (hasMountingOptions) {
                          setActiveMounting(
                            glazingGroups[gName][0].mountingLabel,
                          );
                        }
                      }}
                      suffix={
                        isChecked ? undefined : (
                          <ProductPrice
                            currencyCode={currencyCode}
                            prefix={price >= 0 ? '+' : '-'}
                            price={Math.abs(price)}
                            skipConvertCurrency={!!currencyCode}
                          />
                        )
                      }
                      type="radio"
                      value={gName}
                    />
                  );
                })}
              </CheckboxWrapper>
            </div>

            {hasMountingOptions && (
              <div className="flex flex-col gap-3">
                <div className="flex gap-3">
                  <p className="w-full font-bold">
                    {t('drawers.review.framing.upgrade.mounting.title')}
                  </p>

                  <button
                    className="hyperlink shrink-0"
                    onClick={handleShowMountingGuide}
                    type="button"
                  >
                    {t('drawers.review.framing.upgrade.mounting.guideLink')}
                  </button>
                </div>

                <CheckboxWrapper>
                  {glazingGroups[activeGlazing]?.map(
                    (
                      variant: FrameOptionConsolidated['variants'][number] & {
                        mountingLabel: string;
                      },
                    ) => {
                      const isChecked =
                        activeMounting === variant.mountingLabel;

                      const totalVariantPrice =
                        variant.consolidatedVariants.reduce(
                          (sum, item) =>
                            sum + item.price * (item.quantity ?? 1),
                          0,
                        );

                      const diff = totalVariantPrice - framePrice;

                      const normalisedVariantName = normaliseVariantName(
                        variant.mountingLabel,
                      );

                      // check frameVariantFeatures, as it might not exist
                      const frameVariantFeatures = t(
                        `drawers.review.framing.upgrade.mounting.${normalisedVariantName}.detailCheckbox`,
                        {
                          returnObjects: true,
                        },
                      );
                      const features = Array.isArray(frameVariantFeatures)
                        ? frameVariantFeatures.filter(
                            (feature): feature is string =>
                              typeof feature === 'string',
                          )
                        : undefined;

                      const staticThumbnails: Record<string, string> = {
                        'float-mount': '/images/float-mount-thumbnail.jpg',
                        'window-float-mount':
                          '/images/window-float-mount-thumbnail.jpg',
                        'window-mount': '/images/window-mount-thumbnail.jpg',
                      };
                      const variantImage =
                        variant.consolidatedVariants[0]?.image;
                      const fallback = staticThumbnails[normalisedVariantName];

                      return (
                        <CheckboxCard
                          key={`mountingOptions-${checkboxGroupNameGlazing}-${variant.mountingLabel}`}
                          checked={isChecked}
                          features={features}
                          id={`mountingOptions-${checkboxGroupNameGlazing}-${variant.mountingLabel}`}
                          label={variant.mountingLabel}
                          name={`mountingOptions-${checkboxGroupNameGlazing}`}
                          onChange={() =>
                            setActiveMounting(variant.mountingLabel)
                          }
                          slot={
                            variantImage ? (
                              <div className="grid w-full grid-cols-2 gap-3">
                                <div
                                  aria-hidden
                                  style={{
                                    backgroundImage: `url(${variantImage})`,
                                    backgroundSize: '200% 200%',
                                    backgroundPosition: '100% 100%',
                                  }}
                                  className="aspect-[1/1]"
                                />

                                <Image
                                  alt={`Image for: ${variant.mountingLabel}`}
                                  src={variantImage}
                                  width={384}
                                />
                              </div>
                            ) : (
                              <div className="grid w-full grid-cols-2 gap-3">
                                <Image
                                  alt={`Image for: ${variant.mountingLabel}`}
                                  src={fallback}
                                  width={384}
                                />
                              </div>
                            )
                          }
                          slotVisibleOnChecked
                          suffix={
                            isChecked || diff === 0 ? undefined : (
                              <ProductPrice
                                currencyCode={currencyCode}
                                prefix={diff >= 0 ? '+' : '-'}
                                price={Math.abs(diff)}
                                skipConvertCurrency={!!currencyCode}
                              />
                            )
                          }
                          type="radio"
                          value={variant.mountingLabel}
                        />
                      );
                    },
                  )}
                </CheckboxWrapper>
              </div>
            )}
          </>
        )}
        {/* {selectedFrame && selectedFrame.variants.length > 1 ? (
          <div>
            <p className="font-bold">
              {t('drawers.review.framing.upgrade.title')}
            </p>

            <p className="col-span-12 pb-3 md:pb-4">
              {t('drawers.review.framing.upgrade.subTitle')}{' '}
              <button
                className="hyperlink"
                onClick={handleShowGlazingGuide}
                type="button"
              >
                {t('drawers.review.framing.upgrade.guideLink')}
              </button>
            </p>

            <CheckboxWrapper>
              {selectedFrame?.variants.map((variant) => {
                const extraPrice = variant.consolidatedVariants.reduce(
                  (acc, { price, quantity }) => acc + price * quantity,
                  0,
                );
                const price = extraPrice - framePrice;
                const normalisedVariantName = normaliseVariantName(
                  variant.title,
                );
                const frameVariantFeatures = t(
                  `drawers.review.framing.upgrade.${normalisedVariantName}.detailsCheckbox`,
                  {
                    returnObjects: true,
                  },
                );
                const frameFeatures = Array.isArray(frameVariantFeatures)
                  ? frameVariantFeatures.filter(
                      (feature): feature is string =>
                        typeof feature === 'string',
                    )
                  : undefined;

                const checked = selectedVariant === variant.title;

                return (
                  <CheckboxCard
                    key={`glazingOptions-${checkboxGroupNameGlazing}-${variant.title}`}
                    checked={checked}
                    features={frameFeatures}
                    id={`glazingOptions-${checkboxGroupNameGlazing}-${variant.title}`}
                    label={variant.title}
                    name={`glazingOptions-${checkboxGroupNameGlazing}`}
                    onChange={() => setSelectedVariant(variant.title)}
                    suffix={
                      checked ? undefined : (
                        <ProductPrice
                          currencyCode={currencyCode}
                          prefix={price >= 0 ? '+' : '-'}
                          price={Math.abs(price)}
                          skipConvertCurrency={!!currencyCode}
                        />
                      )
                    }
                    type="radio"
                    value={variant.title}
                  />
                );
              })}
            </CheckboxWrapper>
          </div>
        ) : null} */}
        <dl className="flex justify-between">
          <dt>{t('drawers.review.framing.upgrade.subtotal')}</dt>
          <dd>
            <ProductPrice
              currencyCode={currencyCode}
              price={subtotal}
              skipConvertCurrency={!!currencyCode}
            />
          </dd>
        </dl>
        {hideSubmitButton || !onSubmit ? null : (
          <div className="flex flex-col gap-3">
            <Button
              fullWidth
              label={submitButtonLabel}
              onClick={() => onSubmit(frame)}
              variant="primary"
            />
            {includeUnframed ? (
              <Button
                fullWidth
                label={t('drawers.review.framing.continueUnframed')}
                onClick={() => onSubmit(null)}
                variant="tertiary"
              />
            ) : null}
          </div>
        )}
        <Divider />
        <div className="w-full text-neutral-4">
          <Trans i18nKey="product.drawers.review.framing.note" t={t}>
            Learn more about{' '}
            <a
              className="hyperlink"
              href="https://avantarte.com/insights/guides/our-framing-studio"
              rel="noopener noreferrer"
              target="_blank"
            >
              our bespoke framing studio
            </a>
          </Trans>
        </div>
      </div>
    </>
  );
};

export { FramePicker };
